import React, { useRef, useState } from 'react';
import { Box, Paper, Typography, TextField, Button, Stack, Divider, FormControlLabel, Switch } from '@mui/material';
import ProctoringWidget from '@shaunstapes/proctoring-widget';
import { enterFullscreen } from '../security/fullscreenManager';

const ProctoringWidgetDemo = () => {
  const widgetRef = useRef(null);
  const [userId, setUserId] = useState('14314');
  const [examId, setExamId] = useState('777');
  const [start, setStart] = useState(false);
  const [ended, setEnded] = useState(false);
  const [runSystemCheck, setRunSystemCheck] = useState(false);
  const [closedBookSecurity, setClosedBookSecurity] = useState(true);

  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [systemCheckOk, setSystemCheckOk] = useState(null);
  const [readyForMonitoring, setReadyForMonitoring] = useState(false);
  const [lastViolation, setLastViolation] = useState(null);
  const [transcript, setTranscript] = useState('');

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant="h4" sx={{ mb: 2 }}>ProctoringWidget Demo</Typography>
      <Paper elevation={2} sx={{ p: 2, mb: 3, position: 'relative', zIndex: 4100 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField label="User ID" value={userId} onChange={(e) => setUserId(e.target.value)} size="small" />
          <TextField label="Exam ID" value={examId} onChange={(e) => setExamId(e.target.value)} size="small" />
          <FormControlLabel
            control={(
              <Switch
                checked={closedBookSecurity}
                onChange={(e) => {
                  setClosedBookSecurity(e.target.checked);
                  setStart(false);
                  setEnded(false);
                }}
              />
            )}
            label="closedBookSecurity"
          />
          <Button variant="contained" onClick={() => { enterFullscreen().catch(() => {}); }}>
            Go Fullscreen
          </Button>
          <Button variant="outlined" onClick={() => setRunSystemCheck(true)}>Run System Check</Button>
          <Button variant="contained" onClick={() => { setEnded(false); setStart(true); }} disabled={start || !readyForMonitoring}>Start Monitoring</Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              setStart(false);
              setEnded(true);
            }}
          >
            End Monitoring
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Embed flag: <code>{`closedBookSecurity={${closedBookSecurity}}`}</code>
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
          <Box>
            <Typography variant="subtitle2">Registration:</Typography>
            <Typography variant="body2">{registrationStatus == null ? '...' : registrationStatus ? 'Registered' : 'Not Registered'}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">System Check:</Typography>
            <Typography variant="body2">{systemCheckOk == null ? '...' : systemCheckOk ? 'Passed' : 'Failed'}</Typography>
          </Box>
          <Box sx={{ minWidth: 300 }}>
            <Typography variant="subtitle2">Last Violation:</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{lastViolation ? JSON.stringify(lastViolation, null, 2) : 'None'}</Typography>
          </Box>
        </Stack>
      </Paper>

      <ProctoringWidget
        ref={widgetRef}
        userId={userId}
        examId={examId}
        closedBookSecurity={closedBookSecurity}
        start={start}
        examIsActive={start}
        endExam={ended}
        runSystemCheck={runSystemCheck}
        requireSystemCheck={true}
        onRegistrationStatus={setRegistrationStatus}
        onSystemCheckComplete={(ok) => { setSystemCheckOk(ok); setRunSystemCheck(false); }}
        onReadyForMonitoring={setReadyForMonitoring}
        onStartMonitoringClick={() => { setEnded(false); setStart(true); }}
        onViolation={(v) => setLastViolation(v)}
        onTranscriptUpdate={setTranscript}
        showEndExamButton={true}
        apiBaseUrl={typeof window !== 'undefined' ? window.location.origin : ''}
        websocketUrl={typeof window !== 'undefined' ? window.location.origin : ''}
        sendTranscriptsToServer={true}
        sendViolationsToServer={true}
        showDebug={false}
        showVideo={true}
      />

      <Paper elevation={1} sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Transcript (live)</Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', minHeight: 100 }}>{transcript || '...'}</Typography>
      </Paper>
    </Box>
  );
};

export default ProctoringWidgetDemo;
